<template>
  <v-container fluid class="mis-expedientes-container">
    <v-alert-dialog ref="alert" />

    <v-card elevation="0" class="mis-expedientes-card">
      <v-card-text>
        <v-alert v-if="!loading && puestos.length === 0" type="info" text>
          No tienes puestos actuales o futuros disponibles.
        </v-alert>

        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

        <v-list v-if="puestos.length" two-line class="pa-0">
          <template v-for="(puesto, index) in puestos">
            <v-list-item
              :key="puesto.id_empleado_puesto"
              class="puesto-list-item"
            >
              <v-list-item-icon>
                <v-icon :color="estadoColor(puesto.estado)">mdi-briefcase-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ puesto.nombre_puesto }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ puesto.periodo || construirPeriodo(puesto) }}
                  <span class="mx-2">•</span>
                  {{ puesto.estado_periodo || 'N/D' }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action class="list-action">
                <v-chip
                  small
                  :color="estadoColor(puesto.estado)"
                  :text-color="Number(puesto.estado) === 1 ? 'black' : 'white'"
                  class="mr-3"
                >
                  {{ puesto.estado_nombre || 'Sin expediente' }}
                </v-chip>
                
                <template v-if="Number(puesto.estado) === 3">
                  <v-btn
                    v-if="!puesto.solicitud_cambios_pendiente"
                    color="primary"
                    small
                    outlined
                    :loading="loadingDetalle && selectedPuestoId === puesto.id_empleado_puesto"
                    :disabled="loadingDetalle"
                    @click.stop="selectPuesto(puesto, true)"
                  >
                    <v-icon left small>mdi-file-edit-outline</v-icon>
                    Solicitar cambio
                  </v-btn>
                  <v-tooltip bottom v-else color="black">
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        small
                        color="orange"
                        text-color="white"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon left small>mdi-clock-outline</v-icon>
                        Solicitud pendiente
                      </v-chip>
                    </template>
                    <span>Solicitud de cambios pendiente de revisión.</span>
                  </v-tooltip>
                </template>

                
                <v-btn
                  v-else-if="Number(puesto.estado) === 1"
                  color="primary"
                  small
                  outlined
                  @click.stop="openExpedienteDialog(puesto)"
                >
                  <v-icon left small>mdi-file-upload-outline</v-icon>
                  Cargar información
                </v-btn>
              </v-list-item-action>
            </v-list-item>
            <v-divider v-if="index < puestos.length - 1" :key="`divider-${puesto.id_empleado_puesto}`" />
          </template>
        </v-list>
      </v-card-text>
    </v-card>

    <v-dialog v-model="dialogSolicitud" max-width="75%" persistent>
      <v-card>
        <v-card-title>Solicitar cambio de expediente</v-card-title>
        <v-card-text>
          <v-form ref="solicitudForm">
            <div class="text-subtitle-1 font-weight-bold mb-2">
              Información que deseas cambiar *
            </div>
            <v-row class="ma-0 pa-0">
              <v-col
                class="ma-0 pa-0"
                cols="4"
                v-for="campo in camposAdicionales"
                :key="`campo-adicional-${campo.id}`"
              >
                <v-checkbox
                  v-model="idsCamposAdicionalesSolicitud"
                  :value="campo.id"
                  :label="campo.nombre"
                  :disabled="savingSolicitud"
                  hide-details
                  class="mt-0"
                />
              </v-col>
              <v-col
                class="ma-0 pa-0"
                cols="4"
                v-for="requisito in requisitosConId"
                :key="requisito.id_puesto_expediente_requisito"
              >
                <v-checkbox
                  v-model="idsRequisitosSolicitud"
                  :value="requisito.id_puesto_expediente_requisito"
                  :label="requisito.nombre || requisito.codigo || 'Requisito'"
                  :disabled="savingSolicitud"
                  hide-details
                  class="mt-0"
                />
              </v-col>
            </v-row>

            <br/>
            <v-textarea
              v-model="motivoSolicitud"
              label="Motivo *"
              outlined
              rows="1"
              auto-grow
              counter="500"
              :rules="[rules.required]"
              :disabled="savingSolicitud"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="green" :disabled="savingSolicitud" @click="closeSolicitudDialog">
            Cancelar
          </v-btn>
          <v-btn text color="red" :loading="savingSolicitud" @click="solicitarCambio">
            Enviar solicitud
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <expediente ref="expediente" @saved="getItems" />
  </v-container>
</template>

<script>
import axios from 'axios'
import Expediente from './Expediente.vue'

export default {
  components: {
    Expediente
  },
  data: () => ({
    loading: false,
    loadingDetalle: false,
    savingSolicitud: false,
    puestos: [],
    selectedPuestoId: null,
    detallePuesto: null,
    dialogSolicitud: false,
    motivoSolicitud: '',
    idsRequisitosSolicitud: [],
    idsCamposAdicionalesSolicitud: [],
    camposAdicionales: [
      { id: 1, nombre: 'Estado civil' },
      { id: 2, nombre: 'Número de Seguridad Social' },
      { id: 3, nombre: 'Datos de contacto' },
      { id: 4, nombre: 'Información legal/fiscal' },
      { id: 5, nombre: 'Cuentas bancarias' }
    ],
    rules: {
      required: value => !!(value && value.trim()) || 'El motivo es obligatorio'
    }
  }),

  computed: {
    selectedPuesto() {
      return this.puestos.find(item => item.id_empleado_puesto === this.selectedPuestoId) || null
    },
    requisitos() {
      if (!this.detallePuesto) return []
      return this.detallePuesto.requisitos || []
    },
    requisitosConId() {
      return this.requisitos.filter(item => item.id_puesto_expediente_requisito)
    }
  },

  mounted() {
    this.getItems()
  },

  methods: {
    async getItems() {
      if (this.loading) return

      this.loading = true
      try {
        const response = await axios.get('/RecursosHumanos/ListarMisPuestosExpediente')
        this.puestos = this.getResponseData(response)

        if (this.selectedPuestoId && !this.selectedPuesto) {
          this.selectedPuestoId = null
          this.detallePuesto = null
        }
      } catch (error) {
        this.showError(error, 'No fue posible cargar tus puestos.')
      } finally {
        this.loading = false
      }
    },

    async selectPuesto(puesto, openDialog = false) {
      if (!puesto) return

      this.selectedPuestoId = puesto.id_empleado_puesto
      this.detallePuesto = null
      this.loadingDetalle = true

      try {
        const response = await axios.get(`/RecursosHumanos/ListarRequisitosEmpleadoPuesto/${puesto.id_empleado_puesto}`)
        this.detallePuesto = { requisitos: this.getResponseData(response) }
        if (openDialog && Number(puesto.estado) === 3) this.openSolicitudDialog()
      } catch (error) {
        this.showError(error, 'No fue posible cargar el detalle del expediente.')
      } finally {
        this.loadingDetalle = false
      }
    },

    openExpedienteDialog(puesto) {
      this.$refs.expediente.open(puesto.id_empleado_puesto)
    },

    openSolicitudDialog() {
      if (!this.selectedPuesto || Number(this.selectedPuesto.estado) !== 3) return

      this.motivoSolicitud = ''
      this.idsRequisitosSolicitud = []
      this.idsCamposAdicionalesSolicitud = []
      this.dialogSolicitud = true
    },

    closeSolicitudDialog() {
      this.dialogSolicitud = false
      this.motivoSolicitud = ''
      this.idsRequisitosSolicitud = []
      this.idsCamposAdicionalesSolicitud = []
      if (this.$refs.solicitudForm) this.$refs.solicitudForm.resetValidation()
    },

    async solicitarCambio() {
      const formValid = this.$refs.solicitudForm.validate()
      if (!formValid) return

      if (this.idsRequisitosSolicitud.length === 0 && this.idsCamposAdicionalesSolicitud.length === 0) {
        this.showError(null, 'Debe indicar la información que desea cambiar.')
        return
      }

      this.savingSolicitud = true
      try {
        await axios.post(
          `/RecursosHumanos/SolicitarCambioExpediente/${this.selectedPuestoId}`,
          {
            motivo: this.motivoSolicitud.trim(),
            ids_puesto_expediente_requisitos: this.idsRequisitosSolicitud,
            ids_campos_adicionales: this.idsCamposAdicionalesSolicitud
          }
        )

        this.closeSolicitudDialog()
        this.getItems();
      } catch (error) {
        this.showError(error, 'No fue posible enviar la solicitud de cambio.')
      } finally {
        this.savingSolicitud = false
      }
    },

    construirPeriodo(puesto) {
      return `${puesto.fecha_desde || 'N/D'} - ${puesto.fecha_hasta || 'Indefinido'}`
    },

    estadoColor(estado) {
      if (Number(estado) === 3) return 'green'
      if (Number(estado) === 2) return 'orange'
      if (Number(estado) === 1) return 'orange'
      return 'grey'
    },

    getResponseData(response) {
      const data = response && response.data
      if (Array.isArray(data)) return data
      if (data && Array.isArray(data.data)) return data.data
      if (data && Array.isArray(data.items)) return data.items
      return data || {}
    },

    showError(error, fallback) {
      let message = fallback
      if (error && error.response && error.response.data) {
        const data = error.response.data
        message = data.msg || data.message || data.title || fallback
      }

      this.$refs.alert.setMessage('', message)
      this.$refs.alert.open()
    }
  }
}
</script>

<style scoped>
.mis-expedientes-container {
  width: 100%;
  max-width: none !important;
}

.mis-expedientes-card,
.mis-expedientes-card.v-card {
  border: 0 !important;
  box-shadow: none !important;
}

.puesto-list-item {
  cursor: default;
}

.list-action {
  flex-direction: row;
  align-items: center;
}
</style>
